import React from 'react';
import { 
  IconButton, 
  Tooltip, 
  Box,
  Zoom,
  Badge
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';

// Ripple animation
const ripple = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(2.4);
    opacity: 0;
  }
`;

const MicButtonContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-flex',
}));

const RippleEffect = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  opacity: 0,
  animation: `${ripple} 1.5s infinite`,
}));

const StyledIconButton = styled(IconButton)(({ theme, active }) => ({
  backgroundColor: active ? theme.palette.primary.main : theme.palette.grey[200],
  color: active ? theme.palette.primary.contrastText : theme.palette.text.secondary,
  transition: theme.transitions.create(['background-color', 'transform', 'box-shadow']),
  '&:hover': {
    backgroundColor: active ? theme.palette.primary.dark : theme.palette.grey[300],
    transform: 'scale(1.05)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
  width: 56,
  height: 56,
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
}));

/**
 * Microphone button component with visual feedback
 * @param {Object} props Component props
 * @param {boolean} props.active Whether the microphone is active
 * @param {Function} props.onClick Click handler
 * @param {boolean} props.disabled Whether the button is disabled
 * @param {boolean} props.muted Whether the microphone is muted
 * @returns {JSX.Element} MicrophoneButton component
 */
const MicrophoneButton = ({ 
  active = false, 
  onClick, 
  disabled = false,
  muted = false,
  size = 'large'
}) => {
  return (
    <Tooltip 
      title={active ? 'Stop Recording' : 'Start Recording'} 
      placement="top"
      TransitionComponent={Zoom}
    >
      <MicButtonContainer>
        {active && !muted && (
          <>
            <RippleEffect sx={{ animationDelay: '0s' }} />
            <RippleEffect sx={{ animationDelay: '0.5s' }} />
            <RippleEffect sx={{ animationDelay: '1s' }} />
          </>
        )}
        
        <StyledBadge
          badgeContent={muted ? "X" : 0}
          invisible={!muted}
          overlap="circular"
        >
          <StyledIconButton
            color="primary"
            active={active}
            onClick={onClick}
            disabled={disabled}
            size={size}
            aria-label={active ? 'stop recording' : 'start recording'}
          >
            {muted ? <MicOffIcon /> : <MicIcon />}
          </StyledIconButton>
        </StyledBadge>
      </MicButtonContainer>
    </Tooltip>
  );
};

export default MicrophoneButton;
